import { Lightbulb, TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { DependencyMetric, DependencyMetricUnit } from '@/schemas/commons';

import {
	CARD_CLASS,
	KIND_ICON,
	METER_DANGER_RATIO,
	METER_WARN_RATIO,
	STATUS_LABEL_KEY,
	STATUS_PILL,
} from './Dependencies.constants';
import { DependenciesLoading } from './Dependencies.loading';
import { useDependencies } from './Dependencies.requests';
import { styles } from './Dependencies.styles';
import type { DependencyCardProps, MetricCellProps } from './Dependencies.types';

const BYTE_UNITS = ['KB', 'MB', 'GB', 'TB'];

const formatBytes = (n: number): string => {
	if (n < 1024) return `${n} B`;
	let value = n / 1024;
	let unit = 0;

	while (value >= 1024 && unit < BYTE_UNITS.length - 1) {
		value /= 1024;
		unit += 1;
	}
	const rounded = value >= 100 ? Math.round(value) : Math.round(value * 10) / 10;

	return `${rounded} ${BYTE_UNITS[unit]}`;
};

const formatScalar = (value: number, unit: DependencyMetricUnit | null | undefined): string => {
	if (unit === 'bytes') return formatBytes(value);
	if (unit === 'percent') return `${Math.round(value * 10) / 10} %`;
	return value.toLocaleString();
};

// Meter only renders when there's a capacity to render against.
const meterRatio = (metric: DependencyMetric): number | null => {
	if (metric.value == null || metric.limit == null || metric.limit <= 0) return null;
	return Math.min(metric.value / metric.limit, 1);
};

const meterFillClass = (ratio: number): string => {
	if (ratio >= METER_DANGER_RATIO) return styles.meterFillDanger;
	if (ratio >= METER_WARN_RATIO) return styles.meterFillWarn;
	return styles.meterFillOk;
};

const MetricCell = ({ metric }: MetricCellProps) => {
	const valueText = metric.value == null ? '—' : formatScalar(metric.value, metric.unit);
	const limitText = metric.limit != null ? formatScalar(metric.limit, metric.unit) : null;
	const ratio = meterRatio(metric);

	return (
		<div className={styles.metric}>
			<p className={styles.metricLabel}>{metric.label}</p>
			<p className={styles.metricValue}>
				{valueText}
				{limitText && <span className={styles.metricUnit}> / {limitText}</span>}
			</p>
			{metric.detail && <p className={styles.metricSub}>{metric.detail}</p>}
			{ratio != null && (
				<div className={styles.meter}>
					<div className={meterFillClass(ratio)} style={{ width: `${Math.round(ratio * 100)}%` }} />
				</div>
			)}
		</div>
	);
};

const DependencyCard = ({ dependency }: DependencyCardProps) => {
	const { t } = useTranslation('translation', { keyPrefix: 'dependencies' });
	const Icon = KIND_ICON[dependency.kind];
	const pill = STATUS_PILL[dependency.status];
	const isMuted = dependency.status === 'disabled';
	const roleLabel = dependency.required ? t('ROLE_REQUIRED') : t('ROLE_OPTIONAL');

	return (
		<article className={CARD_CLASS[dependency.status]}>
			<header className={styles.head}>
				<span className={isMuted ? styles.markMuted : styles.mark}>
					<Icon className="h-md w-md" />
				</span>
				<div className="min-w-0 flex-1">
					<div className="flex flex-wrap items-center gap-2xs">
						<h2 className={isMuted ? styles.nameMuted : styles.name}>{dependency.name}</h2>
						<span className={styles.tag}>{dependency.kind}</span>
						<span className={styles.roleLabel}>{roleLabel}</span>
					</div>
				</div>
				<div className="shrink-0 text-right">
					<span className={pill.container}>
						<span className={pill.dot} />
						{t(STATUS_LABEL_KEY[dependency.status])}
					</span>
					<p className={styles.checkMeta}>
						{dependency.latencyMs != null ? t('LATENCY', { ms: dependency.latencyMs }) : t('NOT_CONFIGURED')}
					</p>
				</div>
			</header>

			{dependency.metrics.length > 0 && (
				<div className={styles.metricsGrid}>
					{dependency.metrics.map((metric) => (
						<MetricCell key={metric.key} metric={metric} />
					))}
				</div>
			)}

			<CardFooter dependency={dependency} />
		</article>
	);
};

const CardFooter = ({ dependency }: DependencyCardProps) => {
	const { t } = useTranslation('translation', { keyPrefix: 'dependencies' });
	const { status, message } = dependency;

	if (status === 'up') return null;

	if (status === 'disabled') {
		return (
			<footer className={styles.footerMuted}>
				<TriangleAlert className="h-sm w-sm shrink-0 text-subtle" />
				<p className={styles.footerMutedText}>{message ?? t('DISABLED_HINT')}</p>
			</footer>
		);
	}

	const isDown = status === 'down';

	return (
		<footer className={isDown ? styles.footerDown : styles.footerWarn}>
			<TriangleAlert className={`h-sm w-sm shrink-0 ${isDown ? 'text-red-700' : 'text-amber-700'}`} />
			<p className={isDown ? styles.footerDownText : styles.footerWarnText}>
				{message ?? (isDown ? t('DOWN_HINT') : t('DEGRADED_HINT'))}
			</p>
		</footer>
	);
};

export const Dependencies = () => {
	const { t } = useTranslation('translation', { keyPrefix: 'dependencies' });
	const { data, isLoading, isError } = useDependencies();

	if (isLoading) return <DependenciesLoading />;
	if (isError || !data) {
		return (
			<main className={styles.content}>
				<p className="text-[13px] text-error">{t('ERROR_LOAD')}</p>
			</main>
		);
	}

	const issues = data.dependencies.filter((d) => d.status === 'down' || d.status === 'degraded').length;

	return (
		<>
			<header className={styles.topbar}>
				<nav className="flex items-center gap-3xs text-[12px] text-subtle">
					<span className="font-medium text-default">{t('BREADCRUMB')}</span>
				</nav>
			</header>

			<main className={styles.content}>
				<header className="flex flex-wrap items-end justify-between gap-md">
					<div>
						<p className={styles.pageEyebrow}>{t('EYEBROW')}</p>
						<h1 className={styles.pageH1}>{t('TITLE')}</h1>
						<p className={styles.pageSub}>{t('SUMMARY', { total: data.dependencies.length, issues })}</p>
					</div>
				</header>

				<section className={styles.grid} aria-label={t('TITLE')}>
					{data.dependencies.map((dependency) => (
						<DependencyCard key={`${dependency.kind}-${dependency.name}`} dependency={dependency} />
					))}
				</section>

				<aside className={styles.reference}>
					<Lightbulb className="mt-3xs h-md w-md shrink-0 text-subtle" />
					<div className="flex flex-col gap-3xs">
						<p className="text-[12px] leading-tight text-default">{t('REFERENCE_TEXT')}</p>
						<p className="text-[11px] text-subtle">{t('REFERENCE_NOTE')}</p>
					</div>
				</aside>
			</main>
		</>
	);
};
